import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import statisticsService from '../../services/statisticsService';

interface ReportField {
  id: string;
  label: string;
  category: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  selected: boolean;
}

interface ChartConfig {
  type: 'line' | 'bar' | 'area' | 'pie';
  title: string;
  xAxis?: string;
  yAxis?: string;
  data: any[];
}

const CustomReportBuilder: React.FC = () => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [chartConfigs, setChartConfigs] = useState<ChartConfig[]>([]);
  const [reportTitle, setReportTitle] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [reportViewMode, setReportViewMode] = useState<'table' | 'json'>('table');
  const [savedReports, setSavedReports] = useState<any[]>([]);

  // Charger les rapports sauvegardés au montage du composant
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('customReports') || '[]');
      setSavedReports(saved);
    } catch (error) {
      console.error('Erreur lors du chargement des rapports sauvegardés:', error);
    }
  }, []);

  const availableFields: ReportField[] = [
    // Informations de base
    { id: 'reference', label: 'Référence', category: 'Informations de base', type: 'string', selected: false },
    { id: 'date_heure', label: 'Date et Heure', category: 'Informations de base', type: 'date', selected: false },
    { id: 'statut', label: 'Statut', category: 'Informations de base', type: 'string', selected: false },
    { id: 'type_pesee', label: 'Type de Pesée', category: 'Informations de base', type: 'string', selected: false },
    
    // Produit
    { id: 'produit_nom', label: 'Nom du Produit', category: 'Produit', type: 'string', selected: false },
    { id: 'produit_categorie', label: 'Catégorie Produit', category: 'Produit', type: 'string', selected: false },
    
    // Poids
    { id: 'poids_brut', label: 'Poids Brut (kg)', category: 'Poids', type: 'number', selected: false },
    { id: 'poids_tare', label: 'Poids Tare (kg)', category: 'Poids', type: 'number', selected: false },
    { id: 'poids_net', label: 'Poids Net (kg)', category: 'Poids', type: 'number', selected: false },
    
    // Véhicule
    { id: 'vehicule_immatriculation', label: 'Immatriculation Véhicule', category: 'Véhicule', type: 'string', selected: false },
    { id: 'vehicule_marque', label: 'Marque Véhicule', category: 'Véhicule', type: 'string', selected: false },
    
    // Chauffeur
    { id: 'chauffeur_nom', label: 'Nom Chauffeur', category: 'Chauffeur', type: 'string', selected: false },
    
    // Client
    { id: 'client_nom', label: 'Nom Client', category: 'Client', type: 'string', selected: false },
    
    // Fournisseur
    { id: 'fournisseur_nom', label: 'Nom Fournisseur', category: 'Fournisseur', type: 'string', selected: false },
    
    // Transporteur
    { id: 'transporteur_nom', label: 'Nom Transporteur', category: 'Transporteur', type: 'string', selected: false },
    
    // Exportateur
    { id: 'exportateur_nom', label: 'Nom Exportateur', category: 'Exportateur', type: 'string', selected: false }
  ];

  const chartTypes = [
    { value: 'line', label: 'Graphique Linéaire', icon: 'fas fa-chart-line' },
    { value: 'bar', label: 'Graphique en Barres', icon: 'fas fa-chart-bar' },
    { value: 'area', label: 'Graphique en Aires', icon: 'fas fa-chart-area' },
    { value: 'pie', label: 'Graphique en Secteurs', icon: 'fas fa-chart-pie' }
  ];

  const toggleField = (fieldId: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const addChart = () => {
    if (selectedFields.length < 2) return;
    
    const newChart: ChartConfig = {
      type: 'line',
      title: 'Nouveau Graphique',
      xAxis: selectedFields[0] || '',
      yAxis: selectedFields[1] || '',
      data: []
    };
    setChartConfigs(prev => [...prev, newChart]);
  };

  const removeChart = (index: number) => {
    setChartConfigs(prev => prev.filter((_, i) => i !== index));
  };

  const updateChart = (index: number, updates: Partial<ChartConfig>) => {
    setChartConfigs(prev => prev.map((chart, i) => 
      i === index ? { ...chart, ...updates } : chart
    ));
  };

  const generateReport = async () => {
    if (selectedFields.length === 0) {
      setError('Veuillez sélectionner au moins un champ');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Déterminer le type de rapport basé sur les champs sélectionnés
      let dataType = 'general';
      if (selectedFields.includes('produit_nom') || selectedFields.includes('produit_categorie')) {
        dataType = 'product';
      } else if (selectedFields.includes('client_nom')) {
        dataType = 'client';
      } else if (selectedFields.includes('date_heure')) {
        dataType = 'time_series';
      }

      // Générer le rapport via le backend
      const reportResult = await statisticsService.generateCustomReport({
        title: reportTitle,
        description: reportDescription,
        data_type: dataType,
        filters: {}, // Pas de filtres pour l'instant
        chart_type: chartConfigs.length > 0 ? chartConfigs[0].type : 'line'
      });

      setReportData(reportResult);

      // Mettre à jour les graphiques avec les vraies données
      if (reportResult.data) {
        const updatedCharts = chartConfigs.map(chart => {
          // Transformer les données selon le type de rapport
          let chartData: Array<{ name: string; value: number }> = [];
          
          if (dataType === 'general') {
            // Pour les rapports généraux, data est un objet avec des indicateurs
            const generalData = reportResult.data as any;
            chartData = [
              { name: 'Total Pesées', value: generalData.total_pesées || generalData.total_pesees || 0 },
              { name: 'Complétées', value: generalData.pesées_complétées || generalData.pesees_completees || 0 },
              { name: 'En Cours', value: generalData.pesées_en_cours || generalData.pesees_en_cours || 0 },
              { name: 'Refoulées', value: generalData.pesées_refoulées || generalData.pesees_refoulees || 0 }
            ];
          } else if (Array.isArray(reportResult.data)) {
            // Pour les autres types, data est un tableau
            if (dataType === 'product') {
              chartData = reportResult.data.map((item: any) => ({
                name: item.produit__nom || 'Produit inconnu',
                value: item.total_weight || 0
              }));
            } else if (dataType === 'client') {
              chartData = reportResult.data.map((item: any) => ({
                name: item.client__nom || 'Client inconnu',
                value: item.total_weight || 0
              }));
            } else {
              // Fallback : utiliser les données telles quelles
              chartData = reportResult.data.map((item: any, index: number) => ({
                name: (chart.xAxis && item[chart.xAxis]) ? String(item[chart.xAxis]) : `Item ${index + 1}`,
                value: (chart.yAxis && item[chart.yAxis]) ? Number(item[chart.yAxis]) : 0
              }));
            }
          }
          
          return {
            ...chart,
            data: chartData
          };
        });
        setChartConfigs(updatedCharts);
      }

    } catch (error: any) {
      console.error('Erreur lors de la génération du rapport:', error);
      setError(`Erreur lors de la génération du rapport: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    if (!reportTitle.trim()) {
      setError('Le titre du rapport est requis pour la sauvegarde');
      return;
    }

    if (selectedFields.length === 0) {
      setError('Veuillez sélectionner au moins un champ');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Préparer les données à sauvegarder
      const reportToSave = {
        title: reportTitle,
        description: reportDescription,
        selectedFields,
        chartConfigs,
        createdAt: new Date().toISOString()
      };

      // Sauvegarder dans le localStorage pour l'instant
      const savedReports = JSON.parse(localStorage.getItem('customReports') || '[]');
      savedReports.push(reportToSave);
      localStorage.setItem('customReports', JSON.stringify(savedReports));

      // Mettre à jour la liste des rapports sauvegardés
      setSavedReports([...savedReports, reportToSave]);
      
      // Afficher un message de succès
      setError(null);
      alert('Rapport sauvegardé avec succès !');

    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError(`Erreur lors de la sauvegarde: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderChart = (config: ChartConfig) => {
    if (!config.data || config.data.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <i className="fas fa-chart-bar text-3xl mb-2"></i>
            <p>Aucune donnée disponible</p>
            <p className="text-sm">Générez le rapport pour voir les données</p>
          </div>
        </div>
      );
    }

    const commonProps = {
      width: '100%',
      height: 300,
      data: config.data
    };

    switch (config.type) {
      case 'line':
        return (
          <ResponsiveContainer {...commonProps}>
            <LineChart data={config.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'bar':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={config.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer {...commonProps}>
            <AreaChart data={config.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="value" stroke="#8B5CF6" fill="#8B5CF6" />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer {...commonProps}>
            <PieChart>
              <Pie
                data={config.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {config.data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  const renderReportTable = (data: any) => {
    if (!data) return null;

    // Si c'est un objet avec des indicateurs généraux
    if (data.total_pesées !== undefined || data.total_pesees !== undefined) {
      const indicators = [
        { label: 'Total Pesées', value: data.total_pesées || data.total_pesees || 0 },
        { label: 'Pesées Complétées', value: data.pesées_complétées || data.pesees_completees || 0 },
        { label: 'Pesées En Cours', value: data.pesées_en_cours || data.pesees_en_cours || 0 },
        { label: 'Pesées Refoulées', value: data.pesées_refoulées || data.pesees_refoulees || 0 },
        { label: 'Poids Total (kg)', value: data.poids_total ? `${parseFloat(data.poids_total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}` : '0.00' }
      ];

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Indicateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {indicators.map((indicator, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {indicator.label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {indicator.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Si c'est un tableau de données
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return (
          <div className="text-center py-8 text-gray-500">
            <i className="fas fa-table text-3xl mb-2"></i>
            <p>Aucune donnée disponible</p>
          </div>
        );
      }

      // Obtenir les clés du premier objet pour les en-têtes
      const headers = Object.keys(data[0]);
      
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header, index) => (
                  <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {headers.map((header, colIndex) => (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {typeof row[header] === 'number' 
                        ? row[header].toLocaleString('fr-FR', { minimumFractionDigits: 2 })
                        : row[header] || '-'
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Fallback pour autres types de données
    return (
      <div className="text-center py-8 text-gray-500">
        <i className="fas fa-exclamation-triangle text-3xl mb-2"></i>
        <p>Format de données non supporté</p>
      </div>
    );
  };

  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, ReportField[]>);

  return (
    <div className="space-y-6">
      {/* Rapports sauvegardés */}
      {savedReports.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Rapports Sauvegardés</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedReports.map((report, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <h4 className="font-medium text-gray-900 mb-2">{report.title}</h4>
                <p className="text-sm text-gray-600 mb-2">{report.description || 'Aucune description'}</p>
                <div className="text-xs text-gray-500 mb-3">
                  <i className="fas fa-calendar mr-1"></i>
                  {new Date(report.createdAt).toLocaleDateString('fr-FR')}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setReportTitle(report.title);
                      setReportDescription(report.description);
                      setSelectedFields(report.selectedFields);
                      setChartConfigs(report.chartConfigs);
                    }}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                  >
                    <i className="fas fa-edit mr-1"></i>
                    Charger
                  </button>
                  <button
                    onClick={() => {
                      const updatedReports = savedReports.filter((_, i) => i !== index);
                      setSavedReports(updatedReports);
                      localStorage.setItem('customReports', JSON.stringify(updatedReports));
                    }}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                  >
                    <i className="fas fa-trash mr-1"></i>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration du rapport */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration du Rapport</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre du rapport <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-1">(requis pour la sauvegarde)</span>
            </label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Mon rapport personnalisé"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Description du rapport"
            />
          </div>
        </div>

        {/* Sélection des champs */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Sélection des Champs</h4>
          <div className="space-y-4">
            {Object.entries(fieldsByCategory).map(([category, fields]) => (
              <div key={category} className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-2">{category}</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {fields.map((field) => (
                    <label key={field.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field.id)}
                        onChange={() => toggleField(field.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Création des graphiques */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Graphiques</h3>
          <button
            onClick={addChart}
            disabled={selectedFields.length < 2}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center"
          >
            <i className="fas fa-plus mr-2"></i>
            Ajouter un Graphique
          </button>
        </div>

        {chartConfigs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <i className="fas fa-chart-bar text-3xl mb-2"></i>
            <p>Aucun graphique configuré</p>
            <p className="text-sm">Sélectionnez des champs et ajoutez des graphiques</p>
          </div>
        ) : (
          <div className="space-y-6">
            {chartConfigs.map((chart, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      value={chart.title}
                      onChange={(e) => updateChart(index, { title: e.target.value })}
                      className="px-3 py-1 border border-gray-300 rounded text-sm font-medium"
                      placeholder="Titre du graphique"
                    />
                    <select
                      value={chart.type}
                      onChange={(e) => updateChart(index, { type: e.target.value as any })}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    >
                      {chartTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={chart.xAxis || ''}
                      onChange={(e) => updateChart(index, { xAxis: e.target.value })}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Axe X</option>
                      {selectedFields.map(fieldId => {
                        const field = availableFields.find(f => f.id === fieldId);
                        return (
                          <option key={fieldId} value={fieldId}>
                            {field?.label || fieldId}
                          </option>
                        );
                      })}
                    </select>
                    <select
                      value={chart.yAxis || ''}
                      onChange={(e) => updateChart(index, { yAxis: e.target.value })}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Axe Y</option>
                      {selectedFields.map(fieldId => {
                        const field = availableFields.find(f => f.id === fieldId);
                        return (
                          <option key={fieldId} value={fieldId}>
                            {field?.label || fieldId}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <button
                    onClick={() => removeChart(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  {renderChart(chart)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Affichage des erreurs */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Affichage des résultats */}
      {reportData && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Résultats du Rapport</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setReportViewMode('table')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  reportViewMode === 'table'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <i className="fas fa-table mr-1"></i>
                Tableau
              </button>
              <button
                onClick={() => setReportViewMode('json')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  reportViewMode === 'json'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <i className="fas fa-code mr-1"></i>
                JSON
              </button>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            {reportViewMode === 'table' ? (
              renderReportTable(reportData.data || reportData)
            ) : (
              <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-auto max-h-96">
                {JSON.stringify(reportData, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button 
          onClick={saveReport}
          disabled={loading || !reportTitle.trim() || selectedFields.length === 0}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors flex items-center"
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Sauvegarde...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Sauvegarder
            </>
          )}
        </button>
        <button 
          onClick={generateReport}
          disabled={loading || selectedFields.length === 0}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center"
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Génération...
            </>
          ) : (
            <>
              <i className="fas fa-download mr-2"></i>
              Générer le Rapport
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CustomReportBuilder;
